import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { brl } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { CalendarDays, ListChecks, Briefcase, GraduationCap, Coffee, RotateCcw, AlertOctagon, Eye, ChevronLeft, ChevronRight, X, MoveRight } from "lucide-react";

const TIPO_META = {
    tarefa: { l: "Tarefa", icon: ListChecks, color: "#0F2044" },
    tarefa_vencida: { l: "Tarefa atrasada", icon: AlertOctagon, color: "#EF4444" },
    follow_up: { l: "Follow-up", icon: Briefcase, color: "#F59E0B" },
    reuniao: { l: "Reunião", icon: Coffee, color: "#3B82F6" },
    treinamento: { l: "Treinamento", icon: GraduationCap, color: "#8B5CF6" },
    renovacao: { l: "Renovação", icon: RotateCcw, color: "#10B981" },
    sem_interacao: { l: "Sem interação", icon: Eye, color: "#94A3B8" },
};

const MOVABLE_TIPOS = new Set(["tarefa", "tarefa_vencida"]);

// M6 — cores por encaminhamento: azul pendente · verde concluída · vermelho atrasada · cinza cancelada
function eventChipColor(e) {
    if (e.tipo === "tarefa" || e.tipo === "tarefa_vencida") {
        if (e.task_status === "concluido") return "#10B981";
        if (e.task_status === "nao_se_aplica") return "#94A3B8";
        if (e.atrasada) return "#EF4444";
        return "#3B82F6";
    }
    return (TIPO_META[e.tipo] || TIPO_META.tarefa).color;
}

function isTaskEvent(e) { return e.tipo === "tarefa" || e.tipo === "tarefa_vencida"; }
function isDoneLike(e) { return isTaskEvent(e) && (e.task_status === "concluido" || e.task_status === "nao_se_aplica"); }

export default function Agenda() {
    const [searchParams, setSearchParams] = useSearchParams();
    const view = searchParams.get("view") || "lista";
    const [range, setRange] = useState(searchParams.get("range") || "week");
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [calMonth, setCalMonth] = useState(() => new Date());
    const [editingEvent, setEditingEvent] = useState(null);  // #2 mover tarefa

    async function reload() {
        setLoading(true);
        const fetchRange = view === "calendario" ? "month" : range;
        try {
            const r = await api.get(`/agenda?range=${fetchRange}`);
            setEvents(r.data);
        } finally { setLoading(false); }
    }

    useEffect(() => { reload(); }, [view, range]);

    const grouped = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        const week = new Date(); week.setDate(week.getDate() + 7);
        const weekStr = week.toISOString().slice(0, 10);
        const day = [], wk = [], month = [], overdue = [];
        // Lista mostra apenas trabalho pendente; concluídas/canceladas ficam no calendário (M6)
        events.filter(e => !isDoneLike(e)).forEach(e => {
            if (e.atrasada && e.tipo !== "sem_interacao" && e.data < today) overdue.push(e);
            else if (e.data === today) day.push(e);
            else if (e.data > today && e.data <= weekStr) wk.push(e);
            else if (e.data > today) month.push(e);
            else day.push(e);
        });
        return { overdue, day, wk, month };
    }, [events]);

    function openEditor(event) {
        if (!MOVABLE_TIPOS.has(event.tipo)) return;
        setEditingEvent(event);
    }

    // M7 — drag & drop no calendário: mover a tarefa para o dia solto
    async function handleDropTask(taskId, newDate) {
        try {
            await api.patch(`/tasks/${taskId}`, { override_date: newDate });
        } finally { await reload(); }
    }

    return (
        <div className="space-y-6 fade-in" data-testid="agenda-page">
            <header className="flex items-end justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-medium">Customer Success</div>
                    <h1 className="font-display font-bold text-3xl text-[#0F2044] mt-1">Agenda</h1>
                    <p className="text-sm text-slate-500 mt-1">Tarefas, reuniões, treinamentos, follow-ups, retornos e renovações em um só lugar. Clique numa tarefa para reagendar.</p>
                </div>
                <div className="flex items-center gap-2">
                    {view === "lista" && (
                        <select value={range} onChange={(e) => setRange(e.target.value)} className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white" data-testid="agenda-range">
                            <option value="day">Hoje</option>
                            <option value="week">Próximos 7 dias</option>
                            <option value="month">Próximos 30 dias</option>
                            <option value="all">Tudo</option>
                        </select>
                    )}
                    <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" data-testid="agenda-view-toggle">
                        <button
                            onClick={() => setSearchParams({ view: "lista" })}
                            data-testid="agenda-view-lista"
                            className={`px-3 py-2 text-sm font-medium ${view === "lista" ? "bg-[#0F2044] text-white" : "bg-white text-slate-600"}`}
                        >
                            Lista
                        </button>
                        <button
                            onClick={() => setSearchParams({ view: "calendario" })}
                            data-testid="agenda-view-calendario"
                            className={`px-3 py-2 text-sm font-medium ${view === "calendario" ? "bg-[#0F2044] text-white" : "bg-white text-slate-600"}`}
                        >
                            Calendário
                        </button>
                    </div>
                </div>
            </header>

            {loading ? (
                <div className="text-sm text-slate-500">Carregando agenda…</div>
            ) : view === "lista" ? (
                <div className="space-y-6">
                    {grouped.overdue.length > 0 && (
                        <Section title={`Atrasadas (${grouped.overdue.length})`} accent="#EF4444" testid="agenda-overdue">
                            {grouped.overdue.map((e, i) => <EventRow key={i} event={e} highlight onEdit={openEditor} />)}
                        </Section>
                    )}
                    <Section title={`Hoje (${grouped.day.length})`} accent="#0F2044" testid="agenda-day">
                        {grouped.day.length === 0 ? <EmptyState text="Nada para hoje." /> : grouped.day.map((e, i) => <EventRow key={i} event={e} onEdit={openEditor} />)}
                    </Section>
                    {range !== "day" && (
                        <Section title={`Próximos 7 dias (${grouped.wk.length})`} accent="#3B82F6" testid="agenda-week">
                            {grouped.wk.length === 0 ? <EmptyState text="Nada na próxima semana." /> : grouped.wk.map((e, i) => <EventRow key={i} event={e} onEdit={openEditor} />)}
                        </Section>
                    )}
                    {(range === "month" || range === "all") && (
                        <Section title={`Próximos 30 dias (${grouped.month.length})`} accent="#8B5CF6" testid="agenda-month">
                            {grouped.month.length === 0 ? <EmptyState text="Nada agendado." /> : grouped.month.map((e, i) => <EventRow key={i} event={e} onEdit={openEditor} />)}
                        </Section>
                    )}
                </div>
            ) : (
                <CalendarView events={events} monthDate={calMonth} onChangeMonth={setCalMonth} onEventClick={openEditor} onDropTask={handleDropTask} />
            )}

            {editingEvent && (
                <TaskDetailModal
                    event={editingEvent}
                    onClose={() => setEditingEvent(null)}
                    onSaved={async () => { setEditingEvent(null); await reload(); }}
                />
            )}
        </div>
    );
}

function Section({ title, accent, children, testid }) {
    return (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid={testid}>
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: accent }} />
                <h2 className="font-display font-semibold text-sm text-[#0F2044]">{title}</h2>
            </div>
            <div className="divide-y divide-slate-50">{children}</div>
        </div>
    );
}

function EmptyState({ text }) {
    return <div className="px-5 py-6 text-sm text-slate-500 text-center">{text}</div>;
}

function EventRow({ event, highlight, onEdit }) {
    const meta = TIPO_META[event.tipo] || TIPO_META.tarefa;
    const Icon = meta.icon;
    const movable = MOVABLE_TIPOS.has(event.tipo);
    return (
        <div className={`px-5 py-3 flex items-center gap-3 ${highlight ? "bg-red-50/40" : "hover:bg-slate-50"}`} data-testid={`agenda-event-${event.ref_id}`}>
            <div className="h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: meta.color + "15" }}>
                <Icon className="h-4 w-4" style={{ color: meta.color }} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0F2044] truncate">{event.titulo}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                    <Link to={`/contratos/${event.contract_id}`} className="text-[#0F2044] hover:underline font-medium" data-testid={`agenda-link-${event.ref_id}`}>
                        {event.organizacao_nome}
                    </Link>
                    <span className="mx-1.5">·</span>
                    <span>{meta.l}</span>
                    {event.responsavel && <><span className="mx-1.5">·</span><span>{event.responsavel}</span></>}
                </div>
            </div>
            <div className="text-right text-xs font-mono-tab flex-shrink-0 flex items-center gap-2">
                <div className={`font-semibold ${event.atrasada ? "text-red-600" : "text-[#0F2044]"}`}>{brl(event.data)}</div>
                {movable && (
                    <button
                        onClick={(e) => { e.preventDefault(); onEdit && onEdit(event); }}
                        data-testid={`agenda-move-${event.ref_id}`}
                        title="Reagendar tarefa"
                        className="p-1.5 rounded hover:bg-slate-200 text-slate-500 hover:text-[#0F2044]"
                    >
                        <MoveRight className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}

function CalendarView({ events, monthDate, onChangeMonth, onEventClick, onDropTask }) {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startWeekDay = firstDay.getDay();
    const [dragOverDay, setDragOverDay] = useState(null);  // M7

    const cells = [];
    for (let i = 0; i < startWeekDay; i++) cells.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) cells.push(new Date(year, month, d));
    while (cells.length % 7) cells.push(null);

    const eventsByDay = useMemo(() => {
        const m = {};
        events.forEach(e => { (m[e.data] = m[e.data] || []).push(e); });
        return m;
    }, [events]);

    const meses = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
    const todayStr = new Date().toISOString().slice(0, 10);

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="agenda-calendar">
            <div className="flex items-center justify-between mb-2">
                <button onClick={() => onChangeMonth(new Date(year, month - 1, 1))} data-testid="cal-prev" className="p-2 hover:bg-slate-100 rounded-md">
                    <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="font-display font-semibold text-lg text-[#0F2044]">{meses[month]} {year}</div>
                <button onClick={() => onChangeMonth(new Date(year, month + 1, 1))} data-testid="cal-next" className="p-2 hover:bg-slate-100 rounded-md">
                    <ChevronRight className="h-4 w-4" />
                </button>
            </div>
            {/* M6 — legenda de cores por status + M7 dica de arrastar */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 mb-3" data-testid="cal-legend">
                <Legend color="#3B82F6" label="Pendente / em andamento" />
                <Legend color="#10B981" label="Concluída" />
                <Legend color="#EF4444" label="Atrasada" />
                <Legend color="#94A3B8" label="Cancelada / não se aplica" />
                <span className="ml-auto text-slate-400">Arraste uma tarefa para outro dia para reagendar · clique para abrir</span>
            </div>
            <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">
                {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map(d => <div key={d} className="text-center py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {cells.map((d, i) => {
                    if (!d) return <div key={i} className="aspect-square" />;
                    const dStr = d.toISOString().slice(0, 10);
                    const dayEvents = eventsByDay[dStr] || [];
                    const isToday = dStr === todayStr;
                    const isDragOver = dragOverDay === dStr;
                    return (
                        <div key={i} data-testid={`cal-day-${dStr}`}
                            onDragOver={(ev) => { ev.preventDefault(); setDragOverDay(dStr); }}
                            onDragLeave={() => setDragOverDay(prev => (prev === dStr ? null : prev))}
                            onDrop={(ev) => {
                                ev.preventDefault();
                                setDragOverDay(null);
                                const taskId = ev.dataTransfer.getData("text/task-id");
                                if (taskId && onDropTask) onDropTask(taskId, dStr);
                            }}
                            className={`aspect-square border rounded-md p-1 overflow-hidden text-xs transition-colors ${isDragOver ? "border-[#0F2044] bg-[#0F2044]/10 ring-1 ring-[#0F2044]/40" : isToday ? "border-[#0F2044] bg-[#0F2044]/5" : "border-slate-100"}`}>
                            <div className={`font-semibold text-right ${isToday ? "text-[#0F2044]" : "text-slate-600"}`}>{d.getDate()}</div>
                            <div className="space-y-0.5 mt-1">
                                {dayEvents.slice(0, 3).map((e, idx) => {
                                    const isTask = isTaskEvent(e);
                                    const draggable = isTask && !isDoneLike(e);
                                    const color = eventChipColor(e);
                                    const commonClass = "block px-1 py-0.5 rounded text-[10px] truncate hover:opacity-80 cursor-pointer";
                                    const style = { background: color + "20", color };
                                    const title = `${e.organizacao_nome} — ${e.titulo}${isTask ? " (clique para abrir · arraste para reagendar)" : ""}`;
                                    return isTask ? (
                                        <button
                                            key={idx}
                                            onClick={() => onEventClick && onEventClick(e)}
                                            draggable={draggable}
                                            onDragStart={(ev) => { ev.dataTransfer.setData("text/task-id", e.ref_id); ev.dataTransfer.effectAllowed = "move"; }}
                                            className={`${commonClass} w-full text-left ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${isDoneLike(e) ? "line-through opacity-70" : ""}`}
                                            style={style}
                                            title={title}
                                            data-testid={`cal-event-${e.ref_id}`}
                                        >
                                            {e.organizacao_nome.slice(0, 14)}
                                        </button>
                                    ) : (
                                        <Link
                                            key={idx}
                                            to={`/contratos/${e.contract_id}`}
                                            className={commonClass}
                                            style={style}
                                            title={title}
                                            data-testid={`cal-event-${e.ref_id}`}
                                        >
                                            {e.organizacao_nome.slice(0, 14)}
                                        </Link>
                                    );
                                })}
                                {dayEvents.length > 3 && <div className="text-[9px] text-slate-400 px-1">+{dayEvents.length - 3} mais</div>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function Legend({ color, label }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
            {label}
        </span>
    );
}

// M6 — Modal completo da tarefa: status, observação, nova data e status do cliente
const TASK_STATUS_OPTIONS = [
    ["pendente", "Pendente"],
    ["em_andamento", "Em andamento"],
    ["concluido", "Concluída"],
    ["nao_se_aplica", "Cancelada / não se aplica"],
];

function TaskDetailModal({ event, onClose, onSaved }) {
    const [status, setStatus] = useState(event.task_status || "pendente");
    const [observacao, setObservacao] = useState(event.observacao || "");
    const [newDate, setNewDate] = useState(event.data || "");
    const [clientInfo, setClientInfo] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/contracts/${event.contract_id}`).then(r => setClientInfo(r.data)).catch(() => {});
    }, [event.contract_id]);

    async function save() {
        setSaving(true); setError("");
        try {
            const payload = { observacao };
            if (status !== event.task_status) payload.status = status;
            if (newDate && newDate !== event.data) payload.override_date = newDate;
            await api.patch(`/tasks/${event.ref_id}`, payload);
            await onSaved();
        } catch (e) {
            setError(e?.response?.data?.detail?.toString?.() || "Falha ao salvar a tarefa");
            setSaving(false);
        }
    }

    async function clearOverride() {
        setSaving(true); setError("");
        try {
            await api.patch(`/tasks/${event.ref_id}`, { override_date: "" });
            await onSaved();
        } catch (e) {
            setError("Falha ao restaurar a data padrão");
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" data-testid="move-task-modal">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 fade-in">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Tarefa da jornada</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">{event.titulo}</h3>
                        <div className="text-xs text-slate-500 mt-0.5">
                            <Link to={`/contratos/${event.contract_id}`} className="text-[#0F2044] hover:underline font-medium">{event.organizacao_nome}</Link>
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="move-task-close" className="text-slate-400 hover:text-slate-700">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* M6 — status do cliente ao abrir a tarefa */}
                {clientInfo && (
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-md px-3 py-2 mb-4" data-testid="task-modal-client-status">
                        <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Status do cliente</span>
                        <StatusBadge status={clientInfo.status} />
                        <span className="text-xs text-slate-500">Score {clientInfo.score}</span>
                    </div>
                )}

                <div className="space-y-4">
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Status da tarefa</label>
                        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="task-modal-status-select"
                            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white focus:border-[#0F2044] focus:outline-none">
                            {TASK_STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-400 mt-1">
                            <span><span className="text-[#3B82F6]">●</span> Pendente</span>
                            <span><span className="text-[#10B981]">●</span> Concluída</span>
                            <span><span className="text-[#EF4444]">●</span> Atrasada</span>
                            <span><span className="text-[#94A3B8]">●</span> Cancelada</span>
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Observações</label>
                        <textarea rows={3} value={observacao} onChange={(e) => setObservacao(e.target.value)}
                            placeholder="Registro do encaminhamento, combinados e próximos passos…"
                            data-testid="task-modal-observacao"
                            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#0F2044] focus:ring-1 focus:ring-[#0F2044]/30 focus:outline-none" />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Nova data</label>
                        <input
                            type="date"
                            value={newDate}
                            onChange={(e) => setNewDate(e.target.value)}
                            data-testid="move-task-date-input"
                            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#0F2044] focus:ring-1 focus:ring-[#0F2044]/30 focus:outline-none"
                        />
                        <p className="text-[11px] text-slate-500 mt-1">A tarefa passa a usar essa data em vez do prazo padrão calculado. Você também pode arrastá-la no calendário.</p>
                    </div>
                </div>
                {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mt-3">{error}</div>}
                <div className="flex justify-between items-center gap-2 mt-5">
                    <button
                        onClick={clearOverride}
                        disabled={saving}
                        data-testid="move-task-clear"
                        className="text-xs text-slate-500 hover:text-[#0F2044] underline disabled:opacity-50"
                    >
                        Voltar ao prazo padrão
                    </button>
                    <div className="flex gap-2">
                        <button onClick={onClose} data-testid="move-task-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">Cancelar</button>
                        <button onClick={save} disabled={saving} data-testid="move-task-save" className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white font-semibold rounded-md disabled:opacity-60">
                            {saving ? "Salvando…" : "Salvar"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
